// src/screens/BlogPage.js
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import "./BlogPage.css";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import Logo from "../../img/logo.png";
import bgImage from "../../img/blog_background.png";
import featuredImage from "../../img/blog.png";
import fallbackImage from "../../img/blog1.png";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import API_BASE from "../../config/api";

import berlin from '../../img/company/berlin.png';
import esf from '../../img/company/esf.jpg';
import hwr from '../../img/company/hwr.png';
import sib from '../../img/company/sib.jpg';

function BlogPage() {
  const [activeFilter, setActiveFilter] = useState("All Posts");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const { user, logout } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();

    useEffect(() => {
      window.scrollTo(0, 0);
    }, []);

  // ✅ Fetch published blogs from API
  useEffect(() => {
    const fetchBlogs = async () => {
      setIsLoading(true);
      setError("");
      try {
        const response = await axios.get(`${API_BASE}/blog/`, {
          params: {
            published_only: true,
            limit: 10,
            skip: 0,
          },
          headers: { accept: "application/json" },
        });
        setBlogPosts(response.data);
      } catch (err) {
        setError("Failed to load blog posts. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const handleLogout = () => logout();

  const handleClick = (e) => {
    e.preventDefault();
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        document.getElementById("features")?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      document.getElementById("features")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const filters = [
    "All Posts",
    "DM Commerce",
    "AI & Automation",
    "Revenue & Analytics",
    "Product Updates",
    "Guides",
  ];

  // ✅ Format date from ISO string
  const formatDate = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  // ✅ Estimate read time from content length
  const estimateReadTime = (content = "") => {
    const words = content.trim().split(/\s+/).length;
    const minutes = Math.max(1, Math.round(words / 200));
    return `${minutes} min read`;
  };

  // ✅ Featured post = first published post
  const featuredPost = blogPosts[0] || null;

  // ✅ Rest of posts (after featured)
  const gridPosts = blogPosts.slice(1);

  return (
    <>
      <Helmet>
        <title>Blog - Revexia | Insights on DM Commerce & AI Revenue Systems</title>
        <meta
          name="description"
          content="Insights on DM commerce, buyer intent, AI revenue systems, and automation for the modern operator."
        />
      </Helmet>

      <div className="blogpage">

        {/* ── Navbar ── */}
        <nav className="navbar-custom">
          <div className="navbar-container">
            <div className="navbar-brand-custom">
              <Link to="/"><img src={Logo} alt="Revexia Logo" className="navbar-logo-img" /></Link>
            </div>
            <div className="navbar-center-pill">
              <ul className="nav-menu-pills">
                <li className="nav-item-pill"><Link to="/" className="nav-link-pill">Home</Link></li>
                <li className="nav-item-pill">
                  <a href="/#features" className="nav-link-pill" onClick={handleClick}>Feature</a>
                </li>
                <li className="nav-item-pill nav-item-dropdown">
                  <button className="nav-link-pill nav-dropdown-toggle">
                    Resources
                    <svg width="10" height="6" viewBox="0 0 10 6" fill="none" className="dropdown-arrow">
                      <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  <div className="nav-dropdown-menu">
                    <Link to="/how" className="nav-dropdown-item">How it works</Link>
                    <Link to="/support" className="nav-dropdown-item">Support overview</Link>
                    <Link to="/trust" className="nav-dropdown-item">Trust Center</Link>
                    <Link to="/privacy-policy" className="nav-dropdown-item">Privacy Policy</Link>
                    <Link to="/terms" className="nav-dropdown-item">Terms of Service</Link>
                     <Link to="/facebook-terms" className="nav-dropdown-item">
                                          Facebook Terms
                                        </Link>
                  </div>
                </li>
                <li className="nav-item-pill"><Link to="/blog" className="nav-link-pill">Blog</Link></li>
              </ul>
            </div>
            <div className="navbar-auth-section">
              {user ? (
                <>
                  <Link to="/dashboard" className="btn-nav-outline">Dashboard</Link>
                  <button onClick={handleLogout} className="btn-nav-gradient">Logout</button>
                </>
              ) : (
                <>
                                  <Link to="/join-waitlist" className="btn-nav-gradient">
                                                     Get Started
                                                   </Link>
                <Link to="/auth" className="btn-nav-gradientlogin">Login</Link>
                 </>
              )}
            </div>
            <button className="navbar-mobile-toggle" type="button" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              <span className="navbar-toggler-icon-custom"></span>
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="navbar-mobile-menu">
              <ul className="mobile-nav-list">
                <li><Link to="/" onClick={() => setMobileMenuOpen(false)}>Home</Link></li>
                <li><a href="/#features" onClick={(e) => { setMobileMenuOpen(false); handleClick(e); }}>Feature</a></li>
                <li><Link to="/how" onClick={() => setMobileMenuOpen(false)}>How it works</Link></li>
                <li><Link to="/support" onClick={() => setMobileMenuOpen(false)}>Support overview</Link></li>
                <li><Link to="/trust" onClick={() => setMobileMenuOpen(false)}>Trust Center</Link></li>
                <li><Link to="/blog" onClick={() => setMobileMenuOpen(false)}>Blog</Link></li>
              </ul>
              <div className="mobile-auth-buttons">
                {user ? (
                  <>
                    <Link to="/dashboard" className="btn-nav-outline" onClick={() => setMobileMenuOpen(false)}>Dashboard</Link>
                    <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="btn-nav-gradient">Logout</button>
                  </>
                ) : (
                  <Link to="/auth" className="btn-nav-gradient" onClick={() => setMobileMenuOpen(false)}>Login</Link>
                )}
              </div>
            </div>
          )}
        </nav>

        {/* ── Blog Header ── */}
        <section className="blog-header-section">
          <div className="blog-header-bg-effects">
            <div className="blog-header-bg-image-wrapper">
              <img src={bgImage} alt="" className="blog-header-bg-image" />
            </div>
            <div className="blog-header-gradient-overlay"></div>
          </div>
          <div className="blog-header-container">
            <h1 className="blog-main-title">Blog</h1>
            <p className="blog-subtitle">
              Insights on DM commerce, buyer intent, AI revenue systems, and automation for the modern operator.
            </p>
          </div>
        </section>

        {/* ── Loading State ── */}
        {isLoading && (
          <section className="blog-loading-section">
            <div className="blog-loading-spinner" />
            <p className="blog-loading-text">Loading posts...</p>
          </section>
        )}

        {/* ── Error State ── */}
        {!isLoading && error && (
          <section className="blog-error-section">
            <p className="blog-error-text">⚠ {error}</p>
            <button className="blog-retry-btn" onClick={() => window.location.reload()}>
              Retry
            </button>
          </section>
        )}

        {/* ── Featured Article ── */}
        {!isLoading && !error && featuredPost && (
          <section className="featured-article-section">
            <div className="featured-article-container">
              <div className="featured-article-card">
                <div className="featured-article-image-wrapper">
                  <img
                    src={featuredPost.media_url || featuredImage}
                    alt={featuredPost.title}
                    className="featured-article-image"
                    onError={(e) => (e.target.src = featuredImage)}
                  />
                </div>
                <div className="featured-article-content">
                  <div className="featured-badge-wrapper">
                    <span className="featured-badge">FEATURED</span>
                  </div>
                  <h2 className="featured-article-title">{featuredPost.title}</h2>
                  <p className="featured-article-excerpt">
                    {featuredPost.excerpt || featuredPost.content?.slice(0, 160) + "..."}
                  </p>
                  <div className="featured-article-meta">
                    <div className="meta-item">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M4.66667 2.33333V1M9.33333 2.33333V1M4 5.66667H10M2.33333 12.3333H11.6667C12.403 12.3333 13 11.7364 13 11V4C13 3.26362 12.403 2.66667 11.6667 2.66667H2.33333C1.59695 2.66667 1 3.26362 1 4V11C1 11.7364 1.59695 12.3333 2.33333 12.3333Z" stroke="#9B9C9F" strokeWidth="1.16667" strokeLinecap="round" />
                      </svg>
                      <span>{formatDate(featuredPost.created_at)}</span>
                    </div>
                    <div className="meta-item">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <circle cx="7" cy="7" r="6" stroke="#9B9C9F" strokeWidth="1.16667" />
                        <path d="M7 3.5V7L9.5 8.5" stroke="#9B9C9F" strokeWidth="1.16667" strokeLinecap="round" />
                      </svg>
                      <span>{estimateReadTime(featuredPost.content)}</span>
                    </div>
                  </div>
                  <Link to={`/blog/${featuredPost.id}`} className="featured-read-more">
                    Read article
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M6 12L10 8L6 4" stroke="#5683FF" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ── Empty State ── */}
        {!isLoading && !error && blogPosts.length === 0 && (
          <section className="blog-empty-section">
            <div className="blog-empty-icon">📝</div>
            <h3 className="blog-empty-title">No posts yet</h3>
            <p className="blog-empty-text">Check back soon for new articles.</p>
          </section>
        )}

        {/* ── Filter Section ── */}
        {!isLoading && !error && blogPosts.length > 0 && (
          <section className="blog-filter-section">
            <div className="blog-filter-container">
              {filters.map((filter) => (
                <button
                  key={filter}
                  className={`filter-btn ${activeFilter === filter ? "active" : ""}`}
                  onClick={() => setActiveFilter(filter)}
                >
                  {filter}
                </button>
              ))}
            </div>
          </section>
        )}

        {/* ── Blog Grid ── */}
        {!isLoading && !error && gridPosts.length > 0 && (
          <section className="blog-grid-section">
            <div className="blog-grid-container">
              {gridPosts.map((post) => (
                <article key={post.id} className="blog-card">
                  <Link to={`/blog/${post.id}`} className="blog-card-link-wrapper">
                    <div className="blog-card-image-wrapper">
                      <img
                        src={post.media_url || fallbackImage}
                        alt={post.title}
                        className="blog-card-image"
                        onError={(e) => (e.target.src = fallbackImage)}
                      />
                    </div>
                  </Link>
                  <div className="blog-card-content">
                    <span className="blog-card-category">BLOG</span>
                    <Link to={`/blog/${post.id}`}>
                      <h3 className="blog-card-title">{post.title}</h3>
                    </Link>
                    <p className="blog-card-excerpt">
                      {post.excerpt || post.content?.slice(0, 120) + "..."}
                    </p>
                    <div className="blog-card-footer">
                      <span className="blog-card-meta">
                        {formatDate(post.created_at)} · {estimateReadTime(post.content)}
                      </span>
                      <Link to={`/blog/${post.id}`} className="blog-card-link">
                        Read more
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <path d="M5.25 10.5L8.75 7L5.25 3.5" stroke="#5683FF" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* ── CTA Section ── */}
        <section className="cta-section-final" id="cta">
          <style>{`
            .logo-marquee-wrapper {
              position: relative;
              width: 100%;
              max-width: 100%;
              overflow: hidden;
              margin-top: 48px;
              padding: 16px 0;
            }

            .logo-marquee-track {
              display: flex;
              width: max-content;
              animation: logo-marquee-scroll 30s linear infinite;
              will-change: transform;
            }

            .logo-marquee-group {
              display: flex;
              align-items: center;
              flex-shrink: 0;
            }

            .logo-marquee-item-box {
              width: 160px;
              height: 56px;
              display: flex;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;
            }

            .logo-marquee-item {
              height: 36px;
              width: auto;
              max-width: 140px;
              object-fit: contain;
              opacity: 0.85;
              transition: opacity 0.3s ease;
              display: block;
            }

            .logo-marquee-item:hover {
              opacity: 1;
            }

            @keyframes logo-marquee-scroll {
              from {
                transform: translateX(0);
              }
              to {
                transform: translateX(-25%);
              }
            }
          `}</style>

          <div className="container-cta-final">
            {/* Purple Gradient Blur Background */}
            <div className="cta-gradient-blur"></div>
            {/* CTA Content */}
            <div className="cta-content-wrapper">
              {/* Heading Container */}
              <div className="cta-heading-container">
                <h2 className="cta-main-heading">
                  Start Building Your AI Revenue Brain Today.
                </h2>
              </div>
              {/* Subheading Container */}
           
              {/* CTA Buttons */}
              <div className="cta-buttons-wrapper">
                {/* Primary CTA Button - Get Started */}
                
                  <Link to="/auth" className="contact-btn-primary">
                    <span className="cta-button-text">Get started</span>
                   </Link>
                  
             
                {/* Secondary CTA Button - Watch Demo */}
                <button className="cta-button-secondary">
                  {/* Gradient Overlay */}
                  <div className="button-gradient-overlay"></div>
                  {/* Horizontal Divider */}
                  <div className="button-horizontal-divider"></div>
                  {/* Animated Dots */}
                  <div className="button-dot-glow dot-1"></div>
                  <div className="button-dot-normal dot-2"></div>
                  <div className="button-dot-small dot-3"></div>
                  {/* Button Content */}
                  <div className="cta-button-content">
                    <svg
                      className="play-icon"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M3.33325 2L12.6666 8L3.33325 14V2Z"
                        fill="white"
                        stroke="white"
                        strokeWidth="1.33"
                      />
                    </svg>
                    <a
  href="https://www.youtube.com/watch?v=G7D5c40whQA"
  target="_blank"
  rel="noopener noreferrer"
  style={{ color: "black" }}
>
  <span className="cta-button-text-secondary">
    What is Revexia
  </span>
</a>
                  </div>
                </button>
              </div>
              {/* Partner Logos Marquee - Scrolling Ticker */}
              <div className="logo-marquee-wrapper">
                <div className="logo-marquee-track">
                  {[0, 1, 2, 3].map((groupIndex) => (
                    <div
                      className="logo-marquee-group"
                      key={groupIndex}
                      aria-hidden={groupIndex !== 0}
                    >
                      <div className="logo-marquee-item-box">
                        <img
                          src={berlin}
                          alt={groupIndex === 0 ? "Berlin" : ""}
                          className="logo-marquee-item"
                        />
                      </div>
                      <div className="logo-marquee-item-box">
                        <img
                          src={esf}
                          alt={groupIndex === 0 ? "European Social Fund" : ""}
                          className="logo-marquee-item"
                        />
                      </div>
                      <div className="logo-marquee-item-box">
                        <img
                          src={hwr}
                          alt={groupIndex === 0 ? "HWR Berlin" : ""}
                          className="logo-marquee-item"
                        />
                      </div>
                      <div className="logo-marquee-item-box">
                        <img
                          src={sib}
                          alt={groupIndex === 0 ? "Startup Incubator Berlin" : ""}
                          className="logo-marquee-item"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Footer ── */}
        <footer className="footer-v2" id="contact">
          <div className="container-footer-v2">
            <div className="footer-main-content">
              <div className="footer-brand-section-v2">
                <div className="footer-logo-wrapper">
                  <img src={Logo} alt="Revexia" className="footer-logo-image" />
                </div>
                <p className="footer-brand-description-v2">
                  A platform to turn social media DM engagement into automated revenue with AI-driven personalization.
                </p>
              </div>
              <div className="footer-column-v2">
                <h4 className="footer-column-heading-v2">Informations</h4>
                <ul className="footer-column-links-v2">
                  <li><Link to="/">Home</Link></li>
                  
                </ul>
              </div>
              <div className="footer-column-v2">
                <h4 className="footer-column-heading-v2">Social</h4>
                <ul className="footer-column-links-v2">
                  <li><a href="https://www.facebook.com/share/1AhWEt4FyM/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer">Facebook</a></li>
                  <li><a href="https://www.instagram.com/revexia.ai/" target="_blank" rel="noopener noreferrer">Instagram</a></li>
                  <li><a href="https://www.linkedin.com/company/revexiaai/" target="_blank" rel="noopener noreferrer">LinkedIn</a></li>
                  <li><a href="https://www.instagram.com/revexia.ai" target="_blank" rel="noopener noreferrer">TikTok</a></li>
                </ul>
              </div>
              <div className="footer-column-v2">
                <h4 className="footer-column-heading-v2">Help</h4>
                <ul className="footer-column-links-v2">
                   <li>
                                      <Link to="/contact">Contact</Link>
                                    </li>
                                    
                                    <li>
                                      <Link to="/terms">Terms & Conditions</Link>
                                    </li>
                                    <li>
                                      <Link to="/privacy-policy">Privacy Policy</Link>
                                    </li>
                                    <li>
                                      <Link to="/facebook-terms">Facebook Terms</Link>
                                    </li>
                </ul>
              </div>
            </div>
            <hr className="footer-divider-line-v2" />
            <div className="footer-bottom-v2">
              <p className="footer-copyright-v2">
                ©2026 Revexia. All rights reserved. Developed by{" "}
                <a href="https://softwarebari.com" target="_blank" rel="noopener noreferrer" className="developer-link">Softwarebari</a>.
              </p>
              <div className="footer-legal-links-v2">
                <Link to="/privacy-policy">Privacy Policy</Link>
                <span className="separator-v2">|</span>
                <Link to="/terms">Terms & Conditions</Link>
                <span className="separator-v2">|</span>
                <Link to="/cookies">Cookies Policy</Link>
              </div>
            </div>
          </div>
        </footer>

      </div>
    </>
  );
}

export default BlogPage;
